import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { DescriptionOutlined, Add } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Grid,
	FormControl,
	FormLabel,
	FormHelperText,
	Typography,
	IconButton,
} from '@mui/material';

import { ConfirmationModal } from 'src/components/common/modal/ConfirmationModal';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import { IPatientAssetData } from 'src/features/patient_asset';
import AssetViewer from 'src/features/patient_asset/components/AssetViewer';
import {
	deletePatientAsset,
	PATIENT_ASSET_LOADING_KEY,
} from 'src/features/patient_asset/store/patientAssetThunks';
import {
	IQuestionnaireFormWidgetProps,
	ISubmittedQuestionData,
} from 'src/features/questionnaire/domain/types';
import { AppDispatch } from 'src/store';

import PatientAssetUploadModal from './components/PatientAssetUploadModal';

const PatientAssetWidget: React.FC<IQuestionnaireFormWidgetProps> = ({
	question,
	onValueChange,
	isDisabled,
	error,
	helperText = 'This field is invalid',
	wasSubmitted,
}) => {
	const { siteSlug } = useParams();
	const dispatch = useDispatch<AppDispatch>();

	const widgetKey = `value_${question.type}` as keyof ISubmittedQuestionData;
	const submittedValuesObj = question.submitted_data?.[
		widgetKey
	] as IPatientAssetData[];
	const [patientAssetValues, setPatientAssetValues] = useState<
		IPatientAssetData[] | null
	>(submittedValuesObj || null);
	const [newAssetValues, setNewAssetValues] = useState<
		IPatientAssetData[] | null
	>(null);
	const [isUploadDialogOpen, setIsUploadDialogOpen] =
		useState<boolean>(false);

	const [isAssetViewerOpen, setIsAssetViewerOpen] = useState<boolean>(false);
	const [currentAssetIndex, setCurrentAssetIndex] = useState<number>(0);
	const [selectedAsset, setSelectedAsset] = useState<number | null>(null);

	const isDeletable = useCallback(
		(assetId: number) => {
			return !!(newAssetValues || []).find(
				(asset) => asset.id === assetId,
			);
		},
		[newAssetValues],
	);

	const handleUploadDialogClose = useCallback(() => {
		setIsUploadDialogOpen(false);
	}, []);

	const handleUploadSuccess = useCallback(
		(uploadedAssets: IPatientAssetData[]) => {
			setPatientAssetValues((prev) => {
				const updated = [...(prev ?? []), ...uploadedAssets];
				onValueChange(question, updated, widgetKey);
				return updated;
			});
			setNewAssetValues((prev) =>
				prev ? [...prev, ...uploadedAssets] : [...uploadedAssets],
			);
		},
		[],
	);

	const handleAssetClick = useCallback((index: number) => {
		setCurrentAssetIndex(index);
		setIsAssetViewerOpen(true);
	}, []);

	const handleAssetViewerClose = useCallback(() => {
		setIsAssetViewerOpen(false);
	}, []);

	const handleAssetIndexChange = useCallback((newIndex: number) => {
		setCurrentAssetIndex(newIndex);
	}, []);

	const handleDeleteAsset = (assetId: number) => {
		if (!siteSlug) return;
		dispatch(
			deletePatientAsset({
				assetId,
				siteSlug,
				type: question.type,
				onSuccess: () => {
					setPatientAssetValues((prev) => {
						const updated = prev
							? prev.filter((asset) => assetId !== asset.id)
							: prev;
						onValueChange(question, updated, widgetKey);
						return updated;
					});
					setSelectedAsset(null);
					setNewAssetValues((prev) =>
						prev !== null
							? prev.filter((asset) => assetId !== asset.id)
							: prev,
					);
					snackbar(
						`${question.type} deleted successfully`,
						'success',
					);
				},
			}),
		);
	};

	useEffect(() => {
		if (wasSubmitted?.wasQuestionnaireSaved) {
			setNewAssetValues(null);
		}
	}, [wasSubmitted]);

	return (
		<FormControl
			component='fieldset'
			required={question.is_required}
			disabled={isDisabled}
			error={error}
			sx={{ width: '100%', textAlign: 'left' }}
		>
			<FormLabel component='legend'>{question.text}</FormLabel>

			{patientAssetValues && patientAssetValues.length > 0 && (
				<Box sx={{ mb: 2, mt: 1 }}>
					<Grid container spacing={1}>
						{patientAssetValues.map((asset, index) => (
							<Grid
								item
								key={asset.id}
								sx={{ position: 'relative' }}
							>
								<IconButton
									sx={{
										position: 'absolute',
										display:
											isDisabled || !isDeletable(asset.id)
												? 'none'
												: 'flex',
										top: 0,
										height: 18,
										width: 18,
										right: -8,
										padding: 0,
										backgroundColor: '#F5F5F5',
										cursor: 'pointer',
										zIndex: 1000,
										':hover': {
											backgroundColor: '#E0E0E0',
											color: '#000000',
										},
									}}
									disabled={isDisabled}
									onClick={() => setSelectedAsset(asset.id)}
								>
									<CloseIcon
										sx={{
											fontSize: 18,
											fontWeight: 900,
										}}
									/>
								</IconButton>
								<Box
									onClick={() => handleAssetClick(index)}
									sx={{
										position: 'relative',
										width: 100,
										height: 100,
										border: 1,
										borderStyle: 'dashed',
										borderColor: 'grey.400',
										borderRadius: 1,
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										justifyContent: 'center',
										overflow: 'hidden',
										cursor: 'pointer',
										'&:hover': {
											borderColor: 'primary.main',
											backgroundColor: 'action.hover',
										},
									}}
								>
									{asset.thumbnail_url ? (
										<img
											src={asset.thumbnail_url}
											alt='Patient asset'
											style={{
												width: '100%',
												height: '100%',
												objectFit: 'cover',
											}}
										/>
									) : (
										<>
											<DescriptionOutlined
												sx={{ width: 30, height: 30 }}
											/>
											<Typography
												variant='caption'
												sx={{
													textAlign: 'center',
													wordBreak: 'break-word',
													px: 0.5,
													fontSize: '10px',
												}}
											>
												File
											</Typography>
										</>
									)}
								</Box>
							</Grid>
						))}
					</Grid>
				</Box>
			)}

			{!isDisabled && (
				<Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
					<Button
						onClick={() => setIsUploadDialogOpen(true)}
						variant='outlined'
						component='label'
						startIcon={<Add />}
						size='small'
						disabled={isDisabled}
					>
						Add {question.type}
					</Button>
				</Box>
			)}

			{error && <FormHelperText>{helperText}</FormHelperText>}

			<PatientAssetUploadModal
				isOpen={isUploadDialogOpen}
				question={question}
				closeUploadDialog={handleUploadDialogClose}
				onUploadSuccess={handleUploadSuccess}
			/>

			{patientAssetValues && patientAssetValues.length > 0 && (
				<AssetViewer
					openAsset={isAssetViewerOpen}
					onClose={handleAssetViewerClose}
					assets={patientAssetValues}
					currentIndex={currentAssetIndex}
					onIndexChange={handleAssetIndexChange}
				/>
			)}
			{selectedAsset && (
				<ConfirmationModal
					title={
						<>
							Are you sure you want to delete the {question.type}{' '}
							?
						</>
					}
					modalContent={'This action cannot be undone.'}
					modalOpen={!!selectedAsset}
					handleYes={() => {
						handleDeleteAsset(selectedAsset);
					}}
					loadingKey={PATIENT_ASSET_LOADING_KEY}
					handleNo={() => {
						setSelectedAsset(null);
					}}
					width='480px'
				/>
			)}
		</FormControl>
	);
};

export default PatientAssetWidget;
